import React, { FC, useEffect, useState } from 'react'
import { Route, useHistory, useLocation, useParams } from 'react-router-dom'
import { useEffectOnce } from 'react-use'
import { INLINE_MESSAGE_VARIANTS, InlineMessage } from 'transfix-ui/components/InlineMessage'
import { TopNav } from 'transfix-ui/components/TopNav'
import { Box } from 'transfix-ui/core'
import Skeleton from 'transfix-ui/core/Skeleton'
import { BooleanParam, StringParam, useQueryParams } from 'use-query-params'
import { useFeatureFlagContext } from 'utils/context/FeatureFlagsContext'
import { useMarketplaceContext } from 'utils/context/MarketplaceContext'
import { BidAcceptedModal, BidCounteredModal } from 'apps/fleet-planner/components/bids'
import { BidSubmitModal } from 'apps/fleet-planner/components/bids/BidSubmitModal'
import { ShipmentMap } from 'apps/fleet-planner/components/eld-tracking/ShipmentMap'
import { useLoadDetailsModalStyles } from 'apps/fleet-planner/components/load-details/LoadDetailsModal'
import { useUserContext } from 'apps/fleet-planner/context'
import {
  useBidSubmit,
  useFMSAnalytics,
  useMembershipValidation,
  useTrackConversion,
  useShipmentOffersApi,
} from 'apps/fleet-planner/hooks'
import { BiddingPermissionsModal } from 'apps/fleet-planner/pages/BiddingPermissionsModal'
import { CarrierComplianceModal } from 'apps/fleet-planner/pages/CarrierComplianceModal'
import { BookBidSection } from 'apps/fleet-planner/pages/marketplace/BookBidSection'
import { LoadUnavailable } from 'apps/fleet-planner/pages/marketplace/LoadUnavailable'
import { IPermissionModal } from 'apps/fleet-planner/pages/marketplace/MarketplaceLoadDetailsModal'
import { OpsRepModal } from 'apps/fleet-planner/pages/marketplace/OpsRepModal'
import { BidModal, TBidAction } from 'apps/fleet-planner/pages/marketplace/load-details/BidModal'
import { MarketplaceLoadDetailsSection } from 'apps/fleet-planner/pages/marketplace/load-details/MarketplaceLoadDetailsSection'
import { MarketplaceStopSummary } from 'apps/fleet-planner/pages/marketplace/stops/MarketplaceStopSummary'
import { MarketplaceStops } from 'apps/fleet-planner/pages/marketplace/stops/MarketplaceStops'
import 'apps/fleet-planner/scss/pages/_load-details.scss'
import { AuctionShipmentOpsRep, usePostWaterfallReoffer } from 'apps/fleet-planner/types'
import { declineShipmentOffer } from 'apps/fleet-planner/utils/api'
import { isCarrierAllowedToBookShipment } from 'apps/fleet-planner/utils/isCarrierAllowedToBookShipment'
import { isOfferADedicatedRateOfferedToMoreCarriers } from 'apps/fleet-planner/utils/offerHelpers'
import { Container } from 'components/layout'
import { Card, Toast, useToastState } from 'transfix-ui'
import { DISPLAY } from 'constants/display'

export const MarketplaceLoadDetails: FC = () => {
  const history = useHistory()
  const classes = useLoadDetailsModalStyles()
  const { trackEvent } = useFMSAnalytics()
  const { userData } = useUserContext()
  const { motorCarriers = [] } = userData || {}
  const { shipmentId } = useParams<{ shipmentId: string }>()
  const { pathname = '' } = useLocation()
  const { getFlag } = useFeatureFlagContext()
  const { enabled: fmsCounterBiddingEnabled } = getFlag('fmsCounterBidding')
  const { enabled: isfmsDeclineBidEnabled } = getFlag('fmsDeclineBid')
  const { enabled: isHighRiskFlagEnabled } = getFlag('highRiskLoadsFlag')
  const { enabled: isPreBookingMapEnabled } = getFlag('fmsPrebookingLoadDetailsMap')

  const {
    findAllShipmentOffer: findShipmentOffer,
    getAllShipmentOffers: getShipmentOffers,
    isLoading: isShipmentOffersLoading,
  } = useShipmentOffersApi()

  const {
    activeOpsRep,
    getMarketplaceLoad,
    getMarketplaceShipmentCarrierCompliance,
    isLoadUnavailable,
    isLoading,
    setActiveOpsRep,
    shipmentDetails,
  } = useMarketplaceContext()

  const {
    bidUpdateSuccessHandler,
    clearUpdatedBid,
    isAccepted,
    isCountered,
    isPending,
    updatedBid,
  } = useBidSubmit()

  const { id: shipmentDetailsId, info, stops = [], opsRep } = shipmentDetails || {}
  const { isHighRisk } = info || {}
  const shipmentOffer = findShipmentOffer(shipmentDetailsId)
  const { carrierTotalRate, waterfallTimeLeftMinutes = 0 } = shipmentOffer || {}
  const { getUserTypeForCarrier, userCanBidAndBook } = useMembershipValidation()
  const { clearTrackConversionData } = useTrackConversion()
  const { error, mutate: postWaterfallReOffer } = usePostWaterfallReoffer({ id: shipmentDetailsId })

  // Hides nearby location names on map
  const hideNearbyAttractions = isHighRiskFlagEnabled && isHighRisk
  // Only renderes the map once data is fetched
  const isMapVisible = isPreBookingMapEnabled && shipmentDetailsId

  const navigateToRateConPage = (motorCarrierId?: number) => {
    if (motorCarrierId) {
      history.push(`/book/${shipmentDetailsId}/rate-confirmation?carrierId=${motorCarrierId}`)
    } else {
      history.push(`/book/${shipmentDetailsId}/rate-confirmation`)
    }
  }

  const reOfferLoadAndNavigateToRateConPage = async (motorCarrierId?: number) => {
    try {
      await postWaterfallReOffer()
      navigateToRateConPage(motorCarrierId)
    } catch (e) {
      // no-op
      // error is captured in usePostWaterfallReoffer
    }
  }

  const [isComplianceModalOpen, setIsComplianceModalOpen] = useState<boolean>(false)

  const [isOpsRepModalOpen, setIsOpsRepModalOpen] = useState<boolean>(false)
  const [{ eventSource, isShipmentOffer }] = useQueryParams({
    eventSource: StringParam,
    isShipmentOffer: BooleanParam,
  })

  const openOpsRepModalHandler = (opsRepUser?: AuctionShipmentOpsRep) => {
    trackEvent('Book Load', 'CLICKED_CONTACT_BROKER_CTA')
    if (shipmentDetails?.id && opsRepUser) {
      setActiveOpsRep(opsRepUser)
      setIsOpsRepModalOpen(true)
    }
  }

  const closeOpsRepModalHandler = () => setIsOpsRepModalOpen(false)

  // Fetch load info based on shipmentId provided in the url
  useEffect(() => {
    if (shipmentId) {
      getMarketplaceLoad(shipmentId)
    }
  }, [shipmentId])

  useEffectOnce(() => {
    if (!isShipmentOffersLoading && isShipmentOffer) {
      getShipmentOffers()
    }
  })

  const [permissionsModal, setPermissionsModal] = useState<IPermissionModal>({
    isOpen: false,
    actionType: undefined,
    userType: undefined,
  })

  const closeBidModal = () => {
    clearUpdatedBid()
    getMarketplaceLoad(shipmentId)
  }

  const resetPermissionsModal = () => {
    setPermissionsModal({
      isOpen: false,
      actionType: undefined,
      userType: undefined,
    })
  }
  // Clear track conversion data on back button press.
  useEffect(() => {
    return () => {
      if (history.action === 'POP') {
        clearTrackConversionData()
      }
    }
  }, [clearTrackConversionData, history])

  useEffect(() => {
    if (pathname.includes('/compliance')) {
      setIsComplianceModalOpen(true)
    }
  }, [pathname])

  // Track analytics eventss for viewing load details page.
  useEffectOnce(() => {
    trackEvent('Book Load', 'VIEWED_BOOKABLE_LOAD_PAGE')
  })

  // Track analytics events for viewing an unavailable load.
  useEffect(() => {
    if (!isLoading && isLoadUnavailable) {
      trackEvent('Book Load', 'VIEWED_UNAVAILABLE_LOAD')
    }
  }, [isLoadUnavailable, isLoading])

  // Clear UTM params
  useEffect(() => {
    if (eventSource === 'savedSearchEmail' && (isLoadUnavailable || shipmentDetailsId)) {
      history.push(`/book/${shipmentId}`)
    }
  }, [eventSource, history, isLoadUnavailable, shipmentDetailsId, shipmentId])

  const openBookBidModalHandler = () => {
    /**
     * If user is not a dispatcher, prevent them from proceeding
     */
    if (motorCarriers.length === 1) {
      trackEvent('Book Load', 'CLICKED_BID_ON_BOOKABLE_LOAD')
      const [singleCarrier] = motorCarriers || []
      const userType = getUserTypeForCarrier(singleCarrier.id)

      // If the user is not able to do this, show them a modal
      if (!userCanBidAndBook(singleCarrier.id)) {
        setPermissionsModal({
          isOpen: true,
          userType,
          actionType: 'bid',
        })
        return
      }
    }

    history.push(`/book/${shipmentId}/bid`)
  }

  const closeBidModalHandler = () => {
    history.push(`/book/${shipmentId}`)
  }

  const openComplianceModal = () => setIsComplianceModalOpen(true)
  const closeComplianceModal = () => setIsComplianceModalOpen(false)

  const { closeToast, openToast, toastMessage, toastOpen, toastType } = useToastState()
  const bidSuccessHandler = (action: TBidAction, bidId?: number) => {
    bidUpdateSuccessHandler(shipmentId, bidId)
    if (action === 'delete') {
      openToast({
        toastType: 'default',
        toastMessage: 'Bid Deleted',
      })
    }
  }

  const bidErrorHandler = (action: TBidAction) =>
    openToast({
      toastType: 'alert',
      toastMessage: action === 'submit' ? 'Error Submitting Bid' : 'Error Deleting Bid',
    })

  const declineOfferHandler = async () => {
    if (shipmentDetailsId) {
      const declinedId = await declineShipmentOffer(shipmentDetailsId)
      if (declinedId !== undefined) {
        openToast({
          toastType: 'default',
          toastMessage: 'Declined load.',
        })
        trackEvent('Reserved Load', 'RESERVED_SHIPMENT_DECLINED')
      } else {
        openToast({
          toastType: 'alert',
          toastMessage: 'Failed to decline load. Please try again',
        })
      }
    }
  }

  const declineOfferHandlerNew = (response: string = '') => {
    // declining an offer will be handled within the component following the release of DL O4
    // this function only displays a toast on success and refreshes the offers list
    if (response === 'success') {
      openToast({
        toastType: 'default',
        toastMessage: <span>Offer has been successfully declined.</span>,
      })
    } else {
      openToast({
        toastType: 'alert',
        toastMessage: <span>Offer decline unsuccessful. Please try again.</span>,
      })
    }
  }

  const bookNowHandler = async () => {
    const motorCarrierId = updatedBid?.carrier?.motorCarrierId
    clearUpdatedBid()
    // if user only has a single MC, run a compliance check automatically
    // open the compliance modal if they fail this check
    if (motorCarriers.length === 1) {
      const [singleCarrier] = motorCarriers || []
      const userType = getUserTypeForCarrier(singleCarrier.id)

      /**
       * If user is not a dispatcher, prevent them from proceeding
       */
      if (!userCanBidAndBook(singleCarrier.id)) {
        setPermissionsModal({
          isOpen: true,
          userType,
          actionType: 'book',
        })
        return
      }

      const carrierCompliance = await getMarketplaceShipmentCarrierCompliance(
        shipmentDetailsId,
        singleCarrier.dotNumber,
        singleCarrier.mcNumber
      )

      if (!isCarrierAllowedToBookShipment(carrierCompliance, shipmentDetails)) {
        openComplianceModal()
      } else if (isOfferADedicatedRateOfferedToMoreCarriers(shipmentOffer)) {
        reOfferLoadAndNavigateToRateConPage()
      } else {
        navigateToRateConPage()
      }
    } else if (isOfferADedicatedRateOfferedToMoreCarriers(shipmentOffer)) {
      reOfferLoadAndNavigateToRateConPage(motorCarrierId)
    } else {
      navigateToRateConPage(motorCarrierId)
    }
  }

  return (
    <>
      <TopNav title={`Load # ${shipmentId || DISPLAY.empty}`} />
      {isLoadUnavailable ? (
        <Container className='flex justify-center align-center items-center' hidePadding>
          <LoadUnavailable />
        </Container>
      ) : (
        <Container hidePadding>
          <section className='marketplace-load-details-container'>
            {/* Overview + Summary */}
            <div className='load-details-overview'>
              <Box className={classes.content}>
                <div className={classes.contentInner}>
                  {error && (
                    <div className='pt3'>
                      <InlineMessage
                        type={INLINE_MESSAGE_VARIANTS.ALERT}
                        message='Sorry, something went wrong and we were unable to offer you this load.'
                      />
                    </div>
                  )}
                  {isMapVisible && (
                    <Box mx={3} mt={3}>
                      <ShipmentMap
                        isTrackableStatus={false}
                        hideNearbyAttractions={hideNearbyAttractions}
                      />
                    </Box>
                  )}
                  <Box mt={1}>
                    <MarketplaceStopSummary stops={stops} />
                  </Box>
                </div>
              </Box>
            </div>
            {/* Book Bid */}
            <Box className={classes.content}>
              <Box className='ml4 mr4'>
                <Card>
                  {isShipmentOffersLoading ? (
                    <Box alignContent='center'>
                      <Box paddingY={2}>
                        <Skeleton variant='rectangular' height='20px' width='14%' component='div' />
                        <Box pt={2}>
                          <Skeleton
                            variant='rectangular'
                            height='14px'
                            width='36%'
                            component='div'
                          />
                        </Box>
                        <Box pt={2}>
                          <Skeleton
                            variant='rectangular'
                            height='35px'
                            width='100%'
                            component='div'
                          />
                        </Box>
                      </Box>
                    </Box>
                  ) : (
                    <BookBidSection
                      onClickContactUs={openOpsRepModalHandler}
                      onClickBid={openBookBidModalHandler}
                      onClickBookNow={bookNowHandler}
                      onDeclineHandler={
                        isfmsDeclineBidEnabled ? declineOfferHandlerNew : declineOfferHandler
                      }
                      waterfallTimeLeftMinutes={waterfallTimeLeftMinutes}
                      waterfallOfferRate={carrierTotalRate}
                      isBookNowDisabled={!!error}
                      shipmentOffer={shipmentOffer}
                    />
                  )}
                </Card>
              </Box>
            </Box>
            {/* Details */}
            <div className='marketplace-load-details'>
              <Box p={2}>
                <MarketplaceLoadDetailsSection shipmentDetails={shipmentDetails} />
                <MarketplaceStops stops={stops} />
              </Box>
            </div>
          </section>
        </Container>
      )}
      {!isLoading && (
        <Route exact path={['/book/:shipmentId/bid', '/book/:shipmentId/bid/:bidId']}>
          <BidModal
            onClose={closeBidModalHandler}
            isOpen
            onSuccess={bidSuccessHandler}
            onError={bidErrorHandler}
          />
        </Route>
      )}
      <Toast variation={toastType} open={toastOpen} onClose={closeToast} message={toastMessage} />
      <CarrierComplianceModal
        onClose={closeComplianceModal}
        isOpen={isComplianceModalOpen}
        onSubmit={openBookBidModalHandler}
      />
      <BiddingPermissionsModal
        onClose={resetPermissionsModal}
        isOpen={permissionsModal.isOpen}
        actionType={permissionsModal.actionType}
        userType={permissionsModal.userType}
        opsRep={opsRep}
      />
      <OpsRepModal
        isOpen={isOpsRepModalOpen}
        opsRep={activeOpsRep}
        onClose={closeOpsRepModalHandler}
      />
      <Route exact path='/book/:shipmentId'>
        <BidSubmitModal isOpen={isPending} onClose={closeBidModal} />
        <BidAcceptedModal
          isOpen={isAccepted}
          updatedBid={updatedBid}
          onClose={closeBidModal}
          onBookNow={bookNowHandler}
        />
        {fmsCounterBiddingEnabled && (
          <BidCounteredModal
            isOpen={isCountered}
            updatedBid={updatedBid}
            onClose={closeBidModal}
            onBookNow={bookNowHandler}
          />
        )}
      </Route>
    </>
  )
}
